import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sheet from "../Sheet.jsx";
import CanvasItems from "./CanvasItems.jsx";
import ScrollArea from "../ScrollArea.jsx";
import TabStrip from "../TabStrip.jsx";
import { getCurrentTenantIdSync } from "../../lib/userInfo.js";
import {
    kernelResolverForTenant,
    listKernels,
    subscribeKernels,
} from "../../lib/kernelStore.js";
import {
    bundledCanvasRunConcurrency,
    isBundledCanvasDefinition,
    isStoredCanvasDefinition,
    listCanvasTemplates,
    subscribeCanvasTemplates,
} from "../../lib/canvasTemplateStore.js";
import {
    isStoredTenantViewBody,
    loadTenantViewBody,
} from "../../lib/tenantViewCloudStore.js";
import {
    recordTenantViewOpen,
    recordTenantViewRun,
} from "../../lib/tenantViewsApi.js";
import { normalizeRows } from "../../lib/canvasLayout.js";
import { orderCanvases } from "../../lib/dashboardHome.js";
import {
    INVOCATION_SOURCES,
    RUN_SURFACES,
    createBundledRunContext,
    createStoredRunContext,
    createUnknownRunContext,
    isLegacyLocalCanvas,
    legacyCommandCanvasCellAdapter,
} from "../../lib/kernelResolver.js";
import EmptyState from "../EmptyState.jsx";

// Canvases SCOPE lens — a universal scope lens beside Overview / X-ray /
// Inventory (registered as scope.canvases in appLenses). It renders the
// shipped, read-only built-in canvases (and any the tenant forked/saved) at
// EXACTLY the current drilldown level: each scope-aware kernel re-runs narrowed
// to nav.scopePath, so the same canvas reads tenant-wide at the root and
// endpoint/cluster/provider/user-narrowed once you drill in. Scope plumbing is
// automatic — KernelWidget passes params.scope to runKernel, which calls
// applyScopeToSpec for any scopeMode:"scoped" kernel. Token kernels are
// scopeMode:"global" and run tenant-wide everywhere; their captions say so.
//
// Chrome copies the scope overview's activity surface EXACTLY: a `diagram` Sheet
// (eyebrow + title + expand-to-fullscreen wired via slot.onToggleExpand +
// data-expand-target), a nested TabStrip to switch canvases, and the selection
// rendered on the same black `--plot-bg` panel the chart sits on so the tab
// strip reads as containing it. Selection rides the ?cv=<slug> URL param.
//
// The widget area is a single column in a width-CONSTRAINED ScrollArea
// (overflow-x hidden): the plot panel is size-definite (flex-1) and horizontal
// growth is disabled, so a self-measuring widget (e.g. the treemap) can't loop
// the panel wider/taller — it renders to the bounded width and scrolls.

// Vertical-only scroll: x:hidden prevents a wide widget from pushing the content
// host wider (which fed a treemap resize loop). Frozen so the ScrollArea effect
// doesn't re-run each render.
const SCROLL_OPTS = Object.freeze({ overflow: { x: "hidden", y: "scroll" } });

// Expand / collapse control — mirrors ActivityChart's surface button.
function ExpandButton({ expanded, onToggle }) {
    if (!onToggle) return null;
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? "Collapse" : "Expand"}
            className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
        >
            {expanded ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12l8-8" /><path d="M12 12L4 4" />
                </svg>
            ) : (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" />
                </svg>
            )}
        </button>
    );
}

export default function CanvasesBody({ nav, ctx, slot }) {
    const scopePath = nav?.scopePath || [];
    const tenantId = getCurrentTenantIdSync();
    const { expanded = false, onToggleExpand } = slot || {};

    // Canvases + kernels, kept live so a fork (or any save) shows up without a
    // reload. Built-ins are merged in by the stores; they're always present.
    const [canvases, setCanvases] = useState(() => listCanvasTemplates(tenantId));
    const [kernels, setKernels] = useState(() => listKernels(tenantId));
    useEffect(() => subscribeCanvasTemplates(() => setCanvases(listCanvasTemplates(tenantId))), [tenantId]);
    useEffect(() => subscribeKernels(() => setKernels(listKernels(tenantId))), [tenantId]);

    // Built-ins first (shipped order), then the tenant's own canvases — matches
    // the ?cv= fallback (first canvas).
    const ordered = useMemo(() => orderCanvases(canvases), [canvases]);

    const kernelResolver = useMemo(
        () => kernelResolverForTenant(tenantId),
        [kernels, tenantId],
    );

    // Selected canvas = ?cv=<slug>, set by the tab strip; falls back to the first
    // canvas when unset/invalid.
    const [searchParams, setSearchParams] = useSearchParams();
    const cvParam = searchParams.get("cv");
    const active = useMemo(
        () => ordered.find((c) => c.slug === cvParam) || ordered[0] || null,
        [ordered, cvParam],
    );
    const [bodyLoadError, setBodyLoadError] = useState(null);
    useEffect(() => {
        setBodyLoadError(null);
        if (!active?.tenantViewId || active.bodyLoaded === true) return undefined;
        const controller = new AbortController();
        loadTenantViewBody(active.tenantViewId, {
            tenantId,
            signal: controller.signal,
            withDependencies: true,
        }).catch((error) => {
            if (error?.name !== "AbortError") setBodyLoadError(error);
        });
        return () => controller.abort();
    }, [active?.bodyLoaded, active?.tenantViewId, tenantId]);
    const selectCanvas = useCallback((slug) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (slug) next.set("cv", slug); else next.delete("cv");
            return next;
        }, { replace: false });
    }, [setSearchParams]);

    // Scope passed to every widget. KernelWidget → runKernel reads params.scope
    // and applies it to any scopeMode:"scoped" kernel; [] = tenant-wide. The
    // query-language refinement (ctx.query) folds in as more PROMPT-rooted scope.
    const kernelParams = useMemo(
        () => ({ scope: scopePath, range: ctx?.range || null, query: ctx?.query || "" }),
        // scopePath is owned by nav; serialize for a stable dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(scopePath), ctx?.range, ctx?.query],
    );

    // Clicking a value in a widget drills Explore DEEPER from the current scope
    // (append), so the canvas lens is a pivot surface like Inventory — not the
    // widget's default "open scope in a fresh tab".
    const onOpenScope = useCallback(
        ({ dim, ids }) => {
            if (!dim || !ids?.length) return;
            ctx?.onNavigatePath?.([...scopePath, { dim, ids, id: ids[0] }]);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ctx?.onNavigatePath, JSON.stringify(scopePath)],
    );

    const tabs = useMemo(
        () => ordered.map((c) => ({
            id: c.slug,
            label: c.title || c.slug,
            active: c.slug === active?.slug,
            onSelect: () => selectCanvas(c.slug),
        })),
        [ordered, active, selectCanvas],
    );

    const rows = useMemo(() => normalizeRows(active), [active]);
    const isLegacyCanvas = isLegacyLocalCanvas(active);
    const legacyCellAdapter = isLegacyCanvas
        ? legacyCommandCanvasCellAdapter
        : null;
    const invocationSource = !active || isLegacyCanvas
        ? INVOCATION_SOURCES.UNKNOWN
        : isBundledCanvasDefinition(active)
            ? INVOCATION_SOURCES.BUNDLED
            : isStoredCanvasDefinition(active)
                ? INVOCATION_SOURCES.STORED
                : INVOCATION_SOURCES.UNKNOWN;
    const runContext = useMemo(
        () => {
            const metadata = { surface: RUN_SURFACES.CANVAS };
            if (invocationSource === INVOCATION_SOURCES.BUNDLED) {
                return createBundledRunContext(metadata);
            }
            if (invocationSource === INVOCATION_SOURCES.STORED) {
                return createStoredRunContext(metadata);
            }
            return createUnknownRunContext(metadata);
        },
        [invocationSource],
    );
    const storedActive = isStoredTenantViewBody(active) ? active : null;
    const reportedOpenIdentity = useRef(null);
    useEffect(() => {
        if (reportedOpenIdentity.current === storedActive) return;
        reportedOpenIdentity.current = storedActive;
        if (!storedActive?.tenantViewId) return;
        void recordTenantViewOpen(storedActive.tenantViewId).catch((error) => {
            console.warn("[canvas-lens] open telemetry failed", error);
        });
    }, [storedActive]);
    const reportRunOutcome = useCallback((event) => {
        if (!storedActive?.tenantViewId) return;
        void recordTenantViewRun(storedActive.tenantViewId, {
            outcome: event.outcome,
            reasonCategory: event.reasonCategory,
            analyticsQueryCount: event.analyticsQueryCount ?? 0,
        }).catch((error) => {
            console.warn("[canvas-lens] run telemetry failed", error);
        });
    }, [storedActive]);
    const activeIdentityKey = active
        ? `${active.tenantViewId || active.id || active.slug}:${active.version ?? ""}`
        : "no-canvas";

    return (
        // Top margin between the page tab bar and the surface container.
        <div className="flex h-full min-h-0 min-w-0 flex-col py-[10px]">
            <Sheet
                data-expand-target
                expandable
                diagram
                className="flex-1 min-h-0"
                eyebrow="Canvases"
                title={active?.title || "Canvases"}
                actions={<ExpandButton expanded={expanded} onToggle={onToggleExpand} />}
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    {tabs.length ? (
                        // -mb-[1px] pulls the panel up so the active tab's bottom
                        // mask covers the strip's hairline and merges into it.
                        <div className="-mb-[1px]">
                            <TabStrip tabs={tabs} className="pt-0" />
                        </div>
                    ) : null}
                    {/* The selection sits on the same black --plot-bg panel the
                        chart uses, so the tab strip reads as containing it. */}
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
                        <ScrollArea
                            data-content-scroll
                            className="min-h-0 h-full"
                            contentClassName="p-[10px]"
                            options={SCROLL_OPTS}
                        >
                            {!active ? (
                                <EmptyState
                                    title="No canvases yet"
                                    note="Built-in canvases ship with the dashboard; saved canvases appear here too."
                                />
                            ) : active.tenantViewId && active.bodyLoaded !== true ? (
                                <div
                                    className="grid place-items-center px-[10px] py-[20px] text-[12px] text-[color:var(--muted)]"
                                    aria-busy={bodyLoadError ? undefined : "true"}
                                    role={bodyLoadError ? "alert" : "status"}
                                >
                                    {bodyLoadError
                                        ? `Could not load canvas: ${bodyLoadError.message || bodyLoadError}`
                                        : "Loading canvas…"}
                                </div>
                            ) : (
                                <CanvasItems
                                    key={activeIdentityKey}
                                    rows={rows}
                                    kernelResolver={kernelResolver}
                                    legacyCellAdapter={legacyCellAdapter}
                                    runContext={runContext}
                                    kernelParams={kernelParams}
                                    runtime={active.runtime}
                                    canvasKey={active.tenantViewId || active.id || active.slug}
                                    tenantId={tenantId}
                                    runConcurrency={bundledCanvasRunConcurrency(active)}
                                    onOpenScope={onOpenScope}
                                    onRunOutcome={reportRunOutcome}
                                />
                            )}
                        </ScrollArea>
                    </div>
                </div>
            </Sheet>
        </div>
    );
}
